// Editor draft model for the "Show only when…" rule builder, plus the
// normalization contract between the draft and the stored formats:
// exactly one condition -> legacy parent_id/condition (keeps nested
// placement + follow-up features); anything else (2+ conditions) ->
// visibility_rule with parent_id null.
//
// The draft is a flat list of condition rows under a single root group.
// On hydration, any nested legacy/out-of-band tree is defensively flattened
// to its leaves under the root op. Pure module — unit-tested directly.

import type { VisibilityRule } from '@l2u/shared/progress';

export type CondDraft = {
  kind: 'cond';
  /** Render-only React list key — never persisted. */
  key: string;
  parent_id: string | null;
  /** Raw editable text; parsed by parseConditionValue on save. */
  value: string;
};

export type GroupDraft = {
  kind: 'group';
  key: string;
  op: 'and' | 'or';
  children: CondDraft[];
};

export type VisibilityPayload = {
  parent_id: string | null;
  condition: { value: string | boolean | string[] } | null;
  visibility_rule: VisibilityRule | null;
};

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `k${keySeq}`;
}

export function emptyCond(): CondDraft {
  return { kind: 'cond', key: nextKey(), parent_id: null, value: '' };
}

export function emptyGroup(): GroupDraft {
  return { kind: 'group', key: nextKey(), op: 'and', children: [emptyCond()] };
}

// Moved verbatim from QuestionBuilder.tsx so the payload conversion is
// testable without React.
export function parseConditionValue(raw: string): string | boolean | string[] {
  const trimmed = raw.trim();
  if (trimmed.toLowerCase() === 'true') return true;
  if (trimmed.toLowerCase() === 'false') return false;
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch {
      // fall through
    }
  }
  return trimmed;
}

// Note: numbers stringify and re-parse as strings (parity with the old
// builder's String(condition.value) round-trip); no writer produces numeric
// condition values today.
function valueToInput(value: unknown): string {
  if (Array.isArray(value)) return JSON.stringify(value);
  return String(value);
}

// Depth-first leaf collector — tolerates a nested tree for defensive flatten.
function collectLeaves(rule: VisibilityRule): { q: string; value: unknown }[] {
  if ('op' in rule) return rule.children.flatMap(collectLeaves);
  return [rule];
}

/**
 * Hydrate the editor draft from a stored question. Legacy pair -> one-row
 * group; visibility_rule -> flat group (defensively flattening any nested
 * legacy/out-of-band tree); neither -> null (always visible).
 * The root is always a group (implicit in the UI).
 */
export function questionToRuleDraft(q: {
  parent_id: string | null;
  condition: { value: unknown } | null;
  visibility_rule: VisibilityRule | null;
}): GroupDraft | null {
  if (q.visibility_rule) {
    // Defensive flatten: the write path only produces flat rules, but
    // tolerate nested legacy/out-of-band trees by lifting their leaves.
    const op = 'op' in q.visibility_rule ? q.visibility_rule.op : 'and';
    return {
      kind: 'group',
      key: nextKey(),
      op,
      children: collectLeaves(q.visibility_rule).map((leaf) => ({
        kind: 'cond',
        key: nextKey(),
        parent_id: leaf.q,
        value: valueToInput(leaf.value),
      })),
    };
  }
  if (q.parent_id) {
    return {
      kind: 'group',
      key: nextKey(),
      op: 'and',
      children: [
        {
          kind: 'cond',
          key: nextKey(),
          parent_id: q.parent_id,
          value: q.condition?.value !== undefined ? valueToInput(q.condition.value) : '',
        },
      ],
    };
  }
  return null;
}

/**
 * Clean the draft (drop rows with no parent picked), then apply the
 * normalization contract.
 */
export function draftToVisibilityPayload(draft: GroupDraft | null): VisibilityPayload {
  const cleaned = draft ? cleanGroup(draft) : null;
  if (!cleaned) return { parent_id: null, condition: null, visibility_rule: null };
  if (cleaned.children.length === 1) {
    const cond = cleaned.children[0]!;
    return {
      parent_id: cond.parent_id,
      condition: { value: parseConditionValue(cond.value) },
      visibility_rule: null,
    };
  }
  return {
    parent_id: null,
    condition: null,
    visibility_rule: {
      op: cleaned.op,
      children: cleaned.children.map((c) => ({
        // parent_id is guaranteed non-null here: cleanGroup dropped parentless rows.
        q: c.parent_id!,
        value: parseConditionValue(c.value),
      })),
    },
  };
}

function cleanGroup(group: GroupDraft): GroupDraft | null {
  const children = group.children.filter((c) => c.parent_id !== null);
  if (children.length === 0) return null;
  return { ...group, children };
}
